import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext.jsx'

export default function GameAdminPage() {
  const { user, tokens } = useAuth()
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [newLetter, setNewLetter] = useState('')
  const [newItem, setNewItem] = useState({ type: 'image', name: '' })

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchCategories()
    }
  }, [user])

  const fetchCategories = async () => {
    try {
      const res = await fetch('/api/admin/game/categories/', {
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      })
      const data = await res.json()
      if (data.success) {
        setCategories(data.data.categories)
      }
    } catch (e) {
      console.error('Failed to fetch categories:', e)
    } finally {
      setLoading(false)
    }
  }

  const fetchCategoryItems = async (categoryId) => {
    try {
      const res = await fetch(`/api/admin/game/categories/${categoryId}/items/`, {
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      })
      const data = await res.json()
      if (data.success) {
        setItems(data.data.items)
        setSelectedCategory(data.data.category)
      }
    } catch (e) {
      console.error('Failed to fetch items:', e)
    }
  }

  const createCategory = async (e) => {
    e.preventDefault()
    try {
      const res = await fetch('/api/admin/game/categories/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.accessToken}`
        },
        body: JSON.stringify({ letter: newLetter.toUpperCase() })
      })
      const data = await res.json()
      if (data.success) {
        setNewLetter('')
        fetchCategories()
      } else {
        alert(data.message)
      }
    } catch (e) {
      console.error('Failed to create category:', e)
      alert('Failed to create category')
    }
  }

  const createItem = async (e) => {
    e.preventDefault()
    if (!selectedCategory) return

    try {
      // Create the first card
      const res1 = await fetch('/api/admin/game/items/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.accessToken}`
        },
        body: JSON.stringify({
          categoryId: selectedCategory.id,
          type: newItem.type,
          name: newItem.name.trim()
        })
      })
      const data1 = await res1.json()
      
      if (!data1.success) {
        alert(data1.message)
        return
      }

      // Automatically create the second card (matching pair)
      const res2 = await fetch('/api/admin/game/items/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.accessToken}`
        },
        body: JSON.stringify({
          categoryId: selectedCategory.id,
          type: newItem.type,
          name: newItem.name.trim()
        })
      })
      const data2 = await res2.json()
      
      if (data2.success) {
        setNewItem({ type: 'image', name: '' })
        fetchCategoryItems(selectedCategory.id)
        alert(`Added pair: 2 cards named "${newItem.name.trim()}"`)
      } else {
        alert('Created first card but failed to create matching pair')
        fetchCategoryItems(selectedCategory.id)
      }
    } catch (e) {
      console.error('Failed to create item:', e)
      alert('Failed to create item')
    }
  }

  const deleteItem = async (itemId) => {
    if (!confirm('Delete this item?')) return

    try {
      const res = await fetch(`/api/admin/game/items/${itemId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      })
      const data = await res.json()
      if (data.success) {
        fetchCategoryItems(selectedCategory.id)
      }
    } catch (e) {
      console.error('Failed to delete item:', e)
    }
  }

  const deleteCategory = async (categoryId) => {
    if (!confirm('Delete this category and ALL its items? This cannot be undone.')) return

    try {
      const res = await fetch(`/api/admin/game/categories/${categoryId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      })
      const data = await res.json()
      if (data.success) {
        setSelectedCategory(null)
        setItems([])
        fetchCategories()
      }
    } catch (e) {
      console.error('Failed to delete category:', e)
    }
  }

  if (!user || user.role !== 'superadmin') {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <h2>Access Denied - Superadmin Only</h2>
      </div>
    )
  }

  if (loading) {
    return <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}><p>Loading...</p></div>
  }

  return (
    <div style={{ maxWidth: 1200, margin: '32px auto', padding: '0 20px' }}>
      <h1 style={{ fontSize: 32, fontWeight: 600, marginBottom: 32 }}>Game Content Manager</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 32 }}>
        {/* Left: Categories */}
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Categories</h2>
          
          {/* Create Category Form */}
          <form onSubmit={createCategory} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={newLetter}
                onChange={(e) => setNewLetter(e.target.value)}
                placeholder="Letter (A-Z)"
                maxLength={1}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid var(--elev)',
                  borderRadius: 8,
                  background: 'var(--bg)',
                  color: 'var(--fg)'
                }}
                required
              />
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  background: 'var(--accent)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Add
              </button>
            </div>
          </form>

          {/* Category List */}
          <div style={{ display: 'grid', gap: 8 }}>
            {categories.map(cat => (
              <div
                key={cat.id}
                onClick={() => fetchCategoryItems(cat.id)}
                style={{
                  padding: '12px',
                  background: selectedCategory?.id === cat.id ? 'color-mix(in oklab, var(--bg), var(--fg) 12%)' : 'color-mix(in oklab, var(--bg), var(--fg) 6%)',
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>Letter {cat.letter}</div>
                  <div style={{ fontSize: 13, color: 'var(--muted)' }}>{cat.itemCount} items</div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteCategory(cat.id)
                  }}
                  style={{
                    padding: '4px 8px',
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    fontSize: 12,
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Items */}
        <div>
          {selectedCategory ? (
            <>
              <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>
                Letter {selectedCategory.letter} - Items
              </h2>

              {/* Create Item Form */}
              <form onSubmit={createItem} style={{ marginBottom: 20, padding: 16, background: 'color-mix(in oklab, var(--bg), var(--fg) 4%)', borderRadius: 12 }}>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Type</label>
                    <select
                      value={newItem.type}
                      onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid var(--elev)',
                        borderRadius: 8,
                        background: 'var(--bg)',
                        color: 'var(--fg)'
                      }}
                    >
                      <option value="letter">Letter Card</option>
                      <option value="image">Image Card</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
                      Name (e.g., "Aa", "ant", "apple")
                    </label>
                    <input
                      type="text"
                      value={newItem.name}
                      onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                      placeholder="Item name"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid var(--elev)',
                        borderRadius: 8,
                        background: 'var(--bg)',
                        color: 'var(--fg)'
                      }}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    style={{
                      padding: '10px',
                      background: 'var(--accent)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Add Pair (Creates 2 Cards)
                  </button>
                </div>
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--muted)' }}>
                  <strong>Note:</strong> This will automatically create 2 matching cards.<br />
                  Files should be placed at:<br />
                  • /public/Images/{selectedCategory.letter}/{newItem.name || '[name]'}.png<br />
                  • /public/Audio/{selectedCategory.letter}/{newItem.name || '[name]'}.mp3
                </div>
              </form>

              {/* Items List */}
              <div style={{ display: 'grid', gap: 12 }}>
                {items.map(item => (
                  <div
                    key={item.id}
                    style={{
                      padding: '12px',
                      background: 'color-mix(in oklab, var(--bg), var(--fg) 6%)',
                      borderRadius: 8,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{item.name}</div>
                      <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                        Type: {item.type} • Image: /Images/{selectedCategory.letter}/{item.name}.png
                      </div>
                    </div>
                    <button
                      onClick={() => deleteItem(item.id)}
                      style={{
                        padding: '6px 12px',
                        background: '#dc2626',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 12,
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ minHeight: 400, display: 'grid', placeItems: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>Select a category to manage items</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
